import React from 'react';
import SEO from '../components/SEO';

const CookiePolicy: React.FC = () => {
  return (
    <>
      <SEO
        title="Cookie Policy - SpinHarvester"
        description="Learn about how SpinHarvester uses cookies to enhance your browsing experience and improve our services."
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Cookie Policy'
        }}
      />

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
          <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString('en-GB')}</p>

          <div className="bg-white rounded-xl shadow-lg p-8 prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cookies are small text files that are placed on your device when you visit a website. They are widely 
                used to make websites work more efficiently and provide information to website owners.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Cookies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                SpinHarvester uses cookies to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Remember your preferences and settings</li>
                <li>Analyze website traffic and usage patterns</li>
                <li>Improve website functionality and user experience</li>
                <li>Provide personalized content and recommendations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
              
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Essential Cookies</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  These cookies are necessary for the website to function properly. They enable core functionality such 
                  as security, network management, and accessibility.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Analytics Cookies</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  We use analytics cookies to understand how visitors interact with our website. This helps us improve 
                  our content and user experience. These cookies may be set by third-party services like Google Analytics.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Preference Cookies</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  These cookies remember your choices and preferences (such as language or region) to provide a more 
                  personalized experience.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Marketing Cookies</h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  These cookies track your browsing habits to show you relevant advertisements. They may also be used to 
                  limit the number of times you see an advertisement.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Third-Party Cookies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Some cookies on our website are set by third-party services, such as:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Google Analytics (for website analytics)</li>
                <li>Social media platforms (if you interact with social features)</li>
                <li>Advertising networks (for relevant ad delivery)</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                These third parties may use cookies to collect information about your online activities across different 
                websites. We do not control these cookies, so please refer to the third parties' privacy policies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Managing Cookies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You can control and manage cookies in several ways:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or accept cookies. You can 
                also delete cookies that have already been set. Check your browser's help section for instructions.</li>
                <li><strong>Cookie Consent:</strong> When you first visit our website, you can choose to accept or 
                decline non-essential cookies through our cookie consent banner.</li>
                <li><strong>Opt-Out Tools:</strong> You can use industry opt-out tools to manage cookies from 
                advertising networks.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Please note that blocking or deleting cookies may affect your ability to use certain features of our 
                website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cookie Duration</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cookies can be either "session" cookies (temporary, deleted when you close your browser) or "persistent" 
                cookies (remain on your device until they expire or are deleted). We use both types depending on the 
                purpose of the cookie.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Updates to This Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, 
                operational, or regulatory reasons. Changes will be posted on this page with an updated revision date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have questions about our use of cookies, please contact us through our website.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiePolicy;


